import {Component, HostListener, OnInit, QueryList, ViewChildren} from '@angular/core';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {ActivatedRoute} from '@angular/router';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {DynamicFormItemsService} from '../../../../service/dynamic-form-items.service';
import {StringFormatterService} from '../../../../service/utilities/string-formatter.service';
import {NavigationService} from '../../../../service/navigation.service';
import {Title} from '@angular/platform-browser';
import {AdTypesSettingsService} from '../../../../service/ad-types-settings.service';
import {AccountAdTypesInterface} from '../../../../model/account-ad-types.interface';
import {BusinessCategoryConfigurationsService} from '../../../../service/business-category-configurations.service';
import {PromotionalAdCallToAction} from '../../../../model/promotional-ad-call-to-action';
import {Observable} from 'rxjs';
import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {PromotionalAdsConfigurationInterface} from '../../../../model/promotional-ads-configuration.interface';
import {BusinessSubcategoryInterface} from '../../../../model/business-subcategory.interface';
import {emptyDropdownValidator} from '../../../../validators/empty-dropdown-selection.directive';
import {FeatureFlagGuard} from "../../../../feature-flag.guard";
import {OfferRulesComponent} from './offer-rules/offer-rules.component';
import {ConditionalIncentiveConfiguratorComponent} from './conditional-incentive-configurator/conditional-incentive-configurator.component';
import {UserService} from '../../../../service/user.service';

type FeeValueFormValue = {
  value: number | string | null;
};

type DynamicFeeRowFormValue = {
  value: number | string | null;
};

type LegacyFeeFormValue = {
  id?: number | null;
  type?: string | null;
  value?: number | string | null;
  isPercent?: boolean | null;
  leaseApplication?: string | null;
  financeApplication?: string | null;
  displayName?: string | null;
  enabled?: boolean | null;
  accountBusinessCategoryGuid?: string | null;
  pricingOption?: string | null;
  finalPriceText?: string | null;
};

type FeesConfigurationFormValue = {
  documentFee: FeeValueFormValue;
  licenseFee: FeeValueFormValue;
  titleFee: FeeValueFormValue;
  acquisitionFee: FeeValueFormValue;
  taxes: FeeValueFormValue;
  dispositionFee: FeeValueFormValue;
  customFee: FeeValueFormValue;
  rows?: DynamicFeeRowFormValue[];
};

type BusinessCategoryConfigurationFormValue = {
  feesConfiguration: FeesConfigurationFormValue;
  [key: string]: unknown;
};

@Component({
    selector: 'auto-new-ad-types',
    templateUrl: './new-ad-types.component.html',
    styleUrls: ['./new-ad-types.component.scss'],
    standalone: false
})
export class NewAdTypesComponent implements OnInit {
  @ViewChildren(OfferRulesComponent) offerRulesComponents: QueryList<OfferRulesComponent>;
  @ViewChildren(ConditionalIncentiveConfiguratorComponent) conditionalIncentiveComponents: QueryList<ConditionalIncentiveConfiguratorComponent>;
  private readonly leasePricingBaseDefault = 'discountPrice';
  accountId: number;
  accountAdTypes: AccountAdTypesInterface | null = null;
  businessCategoryConfigurations = null;
  newBusinessCategoryType = null;
  usedBusinessCategoryType = null;
  formItems;
  componentName = 'Vehicle Ads';
  masterFormGroup: UntypedFormGroup | null = null;
  newMoneyFactorMarkupControl: UntypedFormControl;
  usedMoneyFactorMarkupControl: UntypedFormControl;
  leasePaymentTypeControl: UntypedFormControl;
  calculateLeaseFromControl: UntypedFormControl;
  settingsSideNav: MenuItem[];
  promotionalAdMasterFormGroup: UntypedFormGroup | null = null;
  promotionalAdTypesCollapsed: boolean;
  accountServiceCategories: BusinessSubcategoryInterface[];
  promotionalAdsConfigurations = null;
  callsToAction: UntypedFormArray;
  actionOptions: Observable<any> | null;
  promotionalAdTabs: MenuItem[] = [];
  decimalPattern = '^(0(\\.\\d+)?|\\.\\d*[1-9]\\d*)$';
  targetOptions = [
    {label: 'Current Window', value: '_self'},
    {label: 'New Window/Tab', value: '_blank'}
  ];
  promotionalActiveTabValue = null;
  expandIcon = 'pi pi-chevron-right';
  collapseIcon = 'pi pi-chevron-down';
  feeType = [
    {label: 'Include in Payment', value: 'includeInPayment'},
    {label: 'Due at Signing', value: 'dueAtSigning'},
    {label: 'Extra', value: 'extra'},
    {label: 'Not Used', value: 'notUsed'}
  ];
  rebateDisclosureFormatOptions = [
    {value: 'grouped', label: 'Grouped'},
    {value: 'itemized', label: 'Itemized'}
  ];
  leaseSellingPriceOptions = [
    {value: 'discountPrice', label: 'Discount price'},
    {value: 'msrp', label: 'MSRP'}
  ];
  leaseIncentiveTypeOptions = [
    {value: 'subvented fallback', label: 'Subvented (Fallback to Standard)'},
    {value: 'subvented no fallback', label: 'Subvented (No Fallback)'},
    {value: 'standard', label: 'Standard'}
  ];
  financeAprSourceOptions = [
    {value: 'incentiveService', label: 'Incentive Service'},
    {value: 'incentiveServiceFallback', label: 'Incentive Service (Fallback to Account Defaults)'},
    {value: 'accountDefaults', label: 'Account Defaults'}
  ];
  usedFinanceAprSourceOptions = [
    {value: 'accountDefaults', label: 'Account Defaults'}
  ];
  downPaymentBasedOnOptions = [
    {value: 'buy price', label: 'Buy Price'},
    {value: 'msrp', label: 'MSRP'}
  ];
  aprSourceOptions = [
    {value: true, label: 'Incentive Service (Fallback to Account Defaults)'},
    {value: false, label: 'Account Defaults'}
  ];
  calculateLeaseFromOptions = [
    {value: 'leaseDownPayment', label: 'Down Payment'},
    {value: 'dueAtSigning', label: 'Due at Signing'}
  ];
  leasePaymentTypeOptions = [
    {value: 'dollar', label: '$'},
    {value: 'percent', label: '%'}
  ];
  leasePricingBaseOptions = [
    {value: 'discountPrice', label: 'Discount Price'},
    {value: 'msrp', label: 'MSRP'},
    {value: 'capCost', label: 'Cap Cost'}
  ];
  pricingOptions = [
    {value: 'addToSalePrice', label: 'Add Purchase Fee to Sale Price'},
    {value: 'includeInSalePrice', label: 'Purchase Fee is Included in Sale Price'}
    ];
  dirtyOfferRulesForms = 0;
  dirtyConditionalIncentiveForms = 0;
  conditionalIncentiveFormValid = true;
  conditionalIncentiveFeatureFlagEnabled = false;
  displayConditionalIncentivesAsAvailable = false;
  hideLeaseOfferWithDealerAddonEnabled = false;
  hideFinanceOfferWithDealerAddonEnabled = false;
  includeFeesInSellingPriceEnabled = false;
  dynamicFeesTableEnabled = false;
  applicationValues = [
    {
      label: 'Include In Pricing',
      value: 'includeInPricing'
    }
  ];
  private readonly dynamicDefaultFinalPriceText = 'Final Price';
  private readonly dynamicDefaultFeeType = 'documentFee';
  private readonly dynamicCustomFeeType = 'customFee';
  private readonly dynamicDocumentFeeType = 'documentFee';
  private readonly dynamicTaxesType = 'taxes';
  private readonly dynamicPurchaseBehaviorIncludedInSalePrice = 'includeInSalePrice';
  private readonly dynamicApplicationBehaviorIncludeInPayment = 'includeInPayment';
  private readonly documentFeeWarningModalKey = 'documentFeeWarning';
  private readonly documentFeeWarningHeader = 'Document Fee Not Configured';
  private readonly documentFeeWarningMessage = 'A Document Fee has not been configured for this account.\n\nDealer fees that are included in the vehicle selling price may require disclosure in advertising and payment calculations based on federal or state guidelines.\n\nPlease confirm you understand that continuing without a configured Document Fee may result in incomplete fee disclosures.';

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private dynamicFormItemsService: DynamicFormItemsService,
    private formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    private stringFormatter: StringFormatterService,
    private adTypesSettingsService: AdTypesSettingsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private businessCategoryConfigurationsService: BusinessCategoryConfigurationsService,
    private featureFlagGuard: FeatureFlagGuard,
    private userService: UserService
  ) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const firstElement = 0;
    this.titleService.setTitle('Ad Types Settings');
    this.route.paramMap.subscribe((params) => {
      this.accountId = parseInt(params.get('accountId'));
      this.featureFlagGuard.checkFeatureAccess('adTypes', 'settings/ad-types/' + this.accountId);
      this.initNavigation();
      this.getAdTypesAccountSettings()
        .then(() => {
          if (this.accountAdTypes.vehicleAdType.enabled && this.accountId) {
            this.getBusinessCategoryConfigurations(this.accountId);
          }
          if (this.accountId) {
            this.getPromotionalAdsConfigurations(this.accountId);
          }
          this.userService.getMpopFeature('conditionalIncentives').subscribe(
            (feature) => {
              this.conditionalIncentiveFeatureFlagEnabled = feature[firstElement].enabled;
            }
          );
          this.userService.getMpopFeature('displayConditionalIncentivesAsAvailable').subscribe(
            (feature) => {
              this.displayConditionalIncentivesAsAvailable = feature[firstElement].enabled;
              if (this.displayConditionalIncentivesAsAvailable) {
                this.applicationValues = [
                  {
                    label: 'Include In Pricing',
                    value: 'includeInPricing'
                  },
                  {
                    label: 'Display as Conditional',
                    value: 'displayAsAvailable'
                  }
                ];
              }
            }
          );
          this.loadDealerAddonFeatureFlags(firstElement);
          this.loadIncludeFeesInSellingPriceFeatureFlag(firstElement);
          this.loadDynamicFeesTableFeatureFlag(firstElement);
        });
      this.getPromotionalAdTypeSubcategories('');
      this.actionOptions = this.adTypesSettingsService.getActionOptions();
    });
  }

  loadDealerAddonFeatureFlags(firstElement: number): void {
    this.userService.getMpopFeature('hideLeaseOfferWithDealerAddon').subscribe(
      (feature) => {
        this.hideLeaseOfferWithDealerAddonEnabled = feature[firstElement].enabled;
      },
      () => {
        this.hideLeaseOfferWithDealerAddonEnabled = false;
      }
    );

    this.userService.getMpopFeature('hideFinanceOfferWithDealerAddon').subscribe(
      (feature) => {
        this.hideFinanceOfferWithDealerAddonEnabled = feature[firstElement].enabled;
      },
      () => {
        this.hideFinanceOfferWithDealerAddonEnabled = false;
      }
    );
  }

  /**
   * Sets includes fees in selling price feature flag status
   * @param firstElement
   */
  loadIncludeFeesInSellingPriceFeatureFlag(firstElement: number): void {
    this.userService.getMpopFeature('includeFeesInSellingPrice').subscribe(
      (feature) => {
        this.includeFeesInSellingPriceEnabled = feature[firstElement].enabled;
        this.setFeeTypeLabel();
      },
      () => {
        this.includeFeesInSellingPriceEnabled = false;
        this.setFeeTypeLabel();
      }
    );
  }

  loadDynamicFeesTableFeatureFlag(firstElement: number): void
  {
    this.userService.getMpopFeature('dynamicFeesTable').subscribe(
      (feature) => {
        this.dynamicFeesTableEnabled = feature[firstElement].enabled;
      },
      () => {
        this.dynamicFeesTableEnabled = false;
      }
    );
  }

  /**
   * Set fee type label based on include fees in selling price feature flag
   */
  setFeeTypeLabel(): void {
    let includeInPaymentLabel = 'Include in Payment';

    if (this.includeFeesInSellingPriceEnabled) {
      includeInPaymentLabel = 'Include in Selling Price';
    }

    this.feeType[0].label = includeInPaymentLabel;
  }

  /**
   * Init navigation
   */
  initNavigation() {
    this.navigationService.getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => {
        this.settingsSideNav = navItems;
      });
  }

  /**
   * Get ad types account settings -- needed to set this.vehicleAdsEnabled
   */
  getAdTypesAccountSettings() {
    return new Promise(((resolve, reject) => {
      this.adTypesSettingsService.getAdTypesAccountSettings(this.accountId)
        .subscribe(
          (result: AccountAdTypesInterface) => {
            this.accountAdTypes = result;
            this.parseAdTypes(result);
            resolve(null);
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Unable to find account info. Please try again.',
              life: 5000
            });
            reject();
          });
    }));
  }

  /**
   * Gets promotional ad types subcategories
   */
  getPromotionalAdTypeSubcategories(accountBusinessCategoryGuid) {
    this.adTypesSettingsService.getPromotionalAdTypeSubcategories(accountBusinessCategoryGuid)
      .subscribe(
        (result: BusinessSubcategoryInterface[]) => {
          this.accountServiceCategories = result;
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unable to find promotional ads subcategories. Please try again.',
            life: 5000
          });
        });
  }

  /**
   *  This is a dirty function to be able to parse the ad types into the defined interface
   * @param adTypes
   */
  parseAdTypes(adTypes) {
    adTypes.forEach((adType) => {
      this.accountAdTypes.accountId = adType.accountId;
      if (adType.name === 'vehicle') {
        this.accountAdTypes.vehicleAdType = adType;
      } else if (adType.name === 'promotional') {
        this.accountAdTypes.promotionalAdType = adType;
      }
    });
  }

  /**
   * Get account's business category configurations & init the vehicle ads form
   *
   * @param accountId
   */
  getBusinessCategoryConfigurations(accountId) {
    this.businessCategoryConfigurationsService.getBusinessCategoryConfigurations(this.componentName, accountId)
      .subscribe(
        (result) => {
          this.businessCategoryConfigurations = result;
          const empty: any = {};
          if (this.businessCategoryConfigurations['businessCategoryTypes'] !== empty && this.masterFormGroup === null) {
            this.initForm(this.businessCategoryConfigurations['businessCategoryTypes']);
          }
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unable to find business category configurations. Please try again.',
            life: 5000
          });
        });
  }

  /**
   * init form - trigger build FormItems, FormControls, and FormGroups
   */
  initForm(businessCategories: any[]) {
    const formItemsResult = {};
    businessCategories.forEach((businessCategory) => {
      const businessCategoryName = businessCategory.name;
      if  (businessCategoryName === 'New') {
        this.newBusinessCategoryType = businessCategory;
        formItemsResult[businessCategoryName] = this.handleBuildNewForm(businessCategory);
      } else if (businessCategoryName === 'Used') {
        this.usedBusinessCategoryType = businessCategory;
        formItemsResult[businessCategoryName] = this.handleBuildUsedForm(businessCategory);
      }
    });
    this.formItems = formItemsResult;
    businessCategories.forEach((businessCategory) => {
      const businessCategoryName = businessCategory['name'];
      this.formItems[businessCategoryName]['feesConfiguration'] = this.handleBuildFeesForm(businessCategory);
    });
    this.masterFormGroup = this.buildFormGroups(this.formItems);
    this.setIncentiveControlsStatus();
  }

  /**
   * Helper function to build New tab
   * @param businessCategory
   */
  handleBuildNewForm(businessCategory) {
    const configuration = businessCategory['configuration'];
    const businessCategoryFormItemsResult = {}

    businessCategoryFormItemsResult['aprConfiguration'] = new UntypedFormGroup({
      showAprOffer: new UntypedFormControl(configuration.aprConfiguration.showAprOffer),
      useChromeIncentivesAprOffer: new UntypedFormControl(configuration.aprConfiguration.useChromeIncentivesAprOffer)
    });
    businessCategoryFormItemsResult['financeConfiguration'] = new UntypedFormGroup({
      showFinanceOffer: new UntypedFormControl(configuration.financeConfiguration.showFinanceOffer),
      financeAprSource: new UntypedFormControl(configuration.financeConfiguration.financeAprSource),
      downPaymentBasedOn: new UntypedFormControl(configuration.financeConfiguration.downPaymentBasedOn),
      includeFinanceDealerIncentives: new UntypedFormControl(configuration.financeConfiguration.includeFinanceDealerIncentives),
      hideFinanceOfferWithDealerAddon: new UntypedFormControl(
        configuration.financeConfiguration.hideFinanceOfferWithDealerAddon ?? false
      )
    });
    businessCategoryFormItemsResult['generalConfiguration'] = new UntypedFormGroup({
      includeSelectInventoryIncentives: new UntypedFormControl(configuration.generalConfiguration.includeSelectInventoryIncentives),
      rebateDisclosureFormat: new UntypedFormControl(configuration.generalConfiguration.rebateDisclosureFormat),
      availableOffersLabel: new UntypedFormControl(configuration.generalConfiguration.availableOffersLabel, [Validators.required, Validators.maxLength(100)])
    });
    businessCategoryFormItemsResult['incentiveConfiguration'] = new UntypedFormGroup({
      useIncentiveService: new UntypedFormControl(configuration.incentiveConfiguration.useIncentiveService),
      enhancedIncentives: new UntypedFormControl(configuration.incentiveConfiguration.enhancedIncentives),
      useVinSpecificIncentives: new UntypedFormControl(configuration.incentiveConfiguration.useVinSpecificIncentives)
    });
    businessCategoryFormItemsResult['leaseConfiguration'] = new UntypedFormGroup({
      showLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showLeaseOffer),
      showAlternateLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showAlternateLeaseOffer),
      leaseSellingPriceType: new UntypedFormControl(configuration.leaseConfiguration.leaseSellingPriceType),
      includeLeaseDealerIncentives: new UntypedFormControl(configuration.leaseConfiguration.includeLeaseDealerIncentives),
      leaseIncentiveType: new UntypedFormControl(configuration.leaseConfiguration.leaseIncentiveType),
      moneyFactorMarkup: new UntypedFormControl(configuration.leaseConfiguration.moneyFactorMarkup, [Validators.pattern(this.decimalPattern)]),
      calculateLeaseFrom: new UntypedFormControl(configuration.leaseConfiguration.calculateLeaseFrom),
      leaseDownDueAmount: new UntypedFormControl(configuration.leaseConfiguration.leaseDownDueAmount),
      leasePaymentType: new UntypedFormControl(configuration.leaseConfiguration.leasePaymentType),
      leasePricingBase: new UntypedFormControl(
        configuration.leaseConfiguration.leasePricingBase ?? this.leasePricingBaseDefault
      ),
      hideLeaseOfferWithDealerAddon: new UntypedFormControl(
        configuration.leaseConfiguration.hideLeaseOfferWithDealerAddon ?? true
      )
    });
    businessCategoryFormItemsResult['purchaseConfiguration'] = new UntypedFormGroup({
      showPurchaseOffer: new UntypedFormControl(configuration.purchaseConfiguration.showPurchaseOffer),
      showMathBox: new UntypedFormControl(configuration.purchaseConfiguration.showMathBox),
      showSavingsOffer: new UntypedFormControl(configuration.purchaseConfiguration.showSavingsOffer),
      showPreRebateSubtotal: new UntypedFormControl(configuration.purchaseConfiguration.showPreRebateSubtotal),
      showLineItemSuperscripts: new UntypedFormControl(configuration.purchaseConfiguration.showLineItemSuperscripts),
      includePurchaseDealerIncentives: new UntypedFormControl(configuration.purchaseConfiguration.includePurchaseDealerIncentives)
    });

    this.newMoneyFactorMarkupControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.moneyFactorMarkup;
    this.calculateLeaseFromControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.calculateLeaseFrom;
    this.leasePaymentTypeControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.leasePaymentType;

    return businessCategoryFormItemsResult;
  }

  /**
   * Helper function to build Used tab
   * @param businessCategory
   */
  handleBuildUsedForm(businessCategory) {
    const configuration = businessCategory['configuration'];
    const businessCategoryFormItemsResult = {}

    businessCategoryFormItemsResult['aprConfiguration'] = new UntypedFormGroup({
      showAprOffer: new UntypedFormControl(configuration.aprConfiguration.showAprOffer)
    });
    businessCategoryFormItemsResult['financeConfiguration'] = new UntypedFormGroup({
      showFinanceOffer: new UntypedFormControl(configuration.financeConfiguration.showFinanceOffer),
      financeAprSource: new UntypedFormControl(configuration.financeConfiguration.financeAprSource),
      hideFinanceOfferWithDealerAddon: new UntypedFormControl(
        configuration.financeConfiguration.hideFinanceOfferWithDealerAddon ?? false
      )
    });
    businessCategoryFormItemsResult['leaseConfiguration'] = new UntypedFormGroup({
      showLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showLeaseOffer),
      showAlternateLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showAlternateLeaseOffer),
      leaseSellingPriceType: new UntypedFormControl(configuration.leaseConfiguration.leaseSellingPriceType),
      leaseIncentiveType: new UntypedFormControl(configuration.leaseConfiguration.leaseIncentiveType),
      moneyFactorMarkup: new UntypedFormControl(configuration.leaseConfiguration.moneyFactorMarkup, [Validators.pattern(this.decimalPattern)]),
      hideLeaseOfferWithDealerAddon: new UntypedFormControl(
        configuration.leaseConfiguration.hideLeaseOfferWithDealerAddon ?? true
      )
    });
    businessCategoryFormItemsResult['purchaseConfiguration'] = new UntypedFormGroup({
      showPurchaseOffer: new UntypedFormControl(configuration.purchaseConfiguration.showPurchaseOffer),
      showSavingsOffer: new UntypedFormControl(configuration.purchaseConfiguration.showSavingsOffer)
    });

    this.usedMoneyFactorMarkupControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.moneyFactorMarkup;

    return businessCategoryFormItemsResult;
  }

  /**
   * Helper function to build fees form
   * @param businessCategory
   */
  handleBuildFeesForm(businessCategory) {
    const feesConfiguration = businessCategory['configuration']['feesConfiguration'] ?? {};
    const accountBusinessCategoryGuid = businessCategory?.guid ?? null;
    const documentFee = this.getLegacyFeeFormValue(
      feesConfiguration['documentFee'],
      this.dynamicDocumentFeeType,
      accountBusinessCategoryGuid
    );
    const licenseFee = this.getLegacyFeeFormValue(
      feesConfiguration['licenseFee'],
      'licenseFee',
      accountBusinessCategoryGuid
    );
    const titleFee = this.getLegacyFeeFormValue(
      feesConfiguration['titleFee'],
      'titleFee',
      accountBusinessCategoryGuid
    );
    const acquisitionFee = this.getLegacyFeeFormValue(
      feesConfiguration['acquisitionFee'],
      'acquisitionFee',
      accountBusinessCategoryGuid
    );
    const taxes = this.getLegacyFeeFormValue(
      feesConfiguration['taxes'],
      this.dynamicTaxesType,
      accountBusinessCategoryGuid
    );
    const dispositionFee = this.getLegacyFeeFormValue(
      feesConfiguration['dispositionFee'],
      'dispositionFee',
      accountBusinessCategoryGuid
    );
    const customFee = this.getLegacyFeeFormValue(
      feesConfiguration['customFee'],
      this.dynamicCustomFeeType,
      accountBusinessCategoryGuid
    );
    const dynamicFeeRows = this.buildDynamicFeeRowsFormArray(feesConfiguration);

    return new UntypedFormGroup({
      documentFee: this.buildLegacyFeeFormGroup(documentFee),
      licenseFee: this.buildLegacyFeeFormGroup(licenseFee),
      titleFee: this.buildLegacyFeeFormGroup(titleFee),
      acquisitionFee: this.buildLegacyFeeFormGroup(acquisitionFee),
      taxes: this.buildLegacyFeeFormGroup(taxes),
      dispositionFee: this.buildLegacyFeeFormGroup(dispositionFee),
      customFee: this.buildLegacyCustomFeeFormGroup(customFee),
      rows: dynamicFeeRows
    });
  }

  private getLegacyFeeFormValue(
    feeValue: LegacyFeeFormValue | null | undefined,
    feeType: string,
    accountBusinessCategoryGuid: string | null
  ): LegacyFeeFormValue
  {
    const normalizedFeeValue: LegacyFeeFormValue = {
      id: null,
      type: feeType,
      value: null,
      isPercent: false,
      leaseApplication: 'notUsed',
      financeApplication: 'notUsed',
      displayName: null,
      enabled: false,
      accountBusinessCategoryGuid: accountBusinessCategoryGuid,
      pricingOption: 'addToSalePrice',
      finalPriceText: this.dynamicDefaultFinalPriceText
    };

    if (feeValue !== null && feeValue !== undefined) {
      normalizedFeeValue.id = feeValue.id ?? null;
      normalizedFeeValue.value = feeValue.value ?? null;
      normalizedFeeValue.displayName = feeValue.displayName ?? null;
      normalizedFeeValue.accountBusinessCategoryGuid = feeValue.accountBusinessCategoryGuid ?? accountBusinessCategoryGuid;
      normalizedFeeValue.pricingOption = feeValue.pricingOption ?? 'addToSalePrice';
      normalizedFeeValue.finalPriceText = feeValue.finalPriceText ?? this.dynamicDefaultFinalPriceText;

      if (typeof feeValue.type === 'string' && feeValue.type !== '') {
        normalizedFeeValue.type = feeValue.type;
      }

      if (typeof feeValue.isPercent === 'boolean') {
        normalizedFeeValue.isPercent = feeValue.isPercent;
      }

      if (typeof feeValue.leaseApplication === 'string' && feeValue.leaseApplication !== '') {
        normalizedFeeValue.leaseApplication = feeValue.leaseApplication;
      }

      if (typeof feeValue.financeApplication === 'string' && feeValue.financeApplication !== '') {
        normalizedFeeValue.financeApplication = feeValue.financeApplication;
      }

      if (typeof feeValue.enabled === 'boolean') {
        normalizedFeeValue.enabled = feeValue.enabled;
      }
    }

    return normalizedFeeValue;
  }

  private buildLegacyFeeFormGroup(feeValue: LegacyFeeFormValue): UntypedFormGroup
  {
    return new UntypedFormGroup({
      id: new UntypedFormControl(feeValue.id ?? null),
      type: new UntypedFormControl(feeValue.type ?? null),
      value: new UntypedFormControl(feeValue.value ?? null),
      isPercent: new UntypedFormControl(feeValue.isPercent ?? false),
      leaseApplication: new UntypedFormControl(feeValue.leaseApplication ?? 'notUsed'),
      financeApplication: new UntypedFormControl(feeValue.financeApplication ?? 'notUsed'),
      enabled: new UntypedFormControl(false),
      accountBusinessCategoryGuid: new UntypedFormControl(feeValue.accountBusinessCategoryGuid ?? null)
    });
  }

  private buildLegacyCustomFeeFormGroup(feeValue: LegacyFeeFormValue): UntypedFormGroup
  {
    return new UntypedFormGroup({
      id: new UntypedFormControl(feeValue.id ?? null),
      type: new UntypedFormControl(feeValue.type ?? this.dynamicCustomFeeType),
      value: new UntypedFormControl(feeValue.value ?? null),
      displayName: new UntypedFormControl(feeValue.displayName ?? null),
      enabled: new UntypedFormControl(feeValue.enabled ?? false),
      isPercent: new UntypedFormControl(false),
      leaseApplication: new UntypedFormControl('notUsed'),
      financeApplication: new UntypedFormControl('notUsed'),
      accountBusinessCategoryGuid: new UntypedFormControl(feeValue.accountBusinessCategoryGuid ?? null),
      pricingOption: new UntypedFormControl(feeValue.pricingOption ?? 'addToSalePrice'),
      finalPriceText: new UntypedFormControl(feeValue.finalPriceText ?? this.dynamicDefaultFinalPriceText)
    });
  }

  private buildDynamicFeeRowsFormArray(feesConfiguration): UntypedFormArray
  {
    const feeRowControls: UntypedFormGroup[] = [];
    const dynamicFeeRows = this.getDynamicFeeRows(feesConfiguration);

    dynamicFeeRows.forEach((feeRow, index) => {
      feeRowControls.push(this.buildDynamicFeeRowFormGroup(feeRow, index));
    });

    return new UntypedFormArray(feeRowControls);
  }

  private getDynamicFeeRows(feesConfiguration): object[]
  {
    let dynamicFeeRows = [];

    if (feesConfiguration !== null && feesConfiguration !== undefined && Array.isArray(feesConfiguration.rows)) {
      dynamicFeeRows = feesConfiguration.rows;
    }

    return dynamicFeeRows;
  }

  private buildDynamicFeeRowFormGroup(feeRow, index: number): UntypedFormGroup
  {
    let feeType = this.getDefaultDynamicFeeType();
    let isPercent = false;
    let isDefaultDocumentFee = false;
    let purchaseBehavior = this.dynamicPurchaseBehaviorIncludedInSalePrice;
    let leaseBehavior = this.dynamicApplicationBehaviorIncludeInPayment;
    let financeBehavior = this.dynamicApplicationBehaviorIncludeInPayment;
    let enabled = true;
    let finalPriceText = this.dynamicDefaultFinalPriceText;
    const customFeeName = this.resolveDynamicCustomFeeName(feeRow);

    if (typeof feeRow?.type === 'string' && feeRow.type !== '') {
      feeType = feeRow.type;
    }

    if (feeRow?.isPercent === true || feeType === this.dynamicTaxesType) {
      isPercent = true;
    }

    if (feeRow?.isDefaultDocumentFee === true) {
      isDefaultDocumentFee = true;
    }

    if (typeof feeRow?.purchaseBehavior === 'string' && feeRow.purchaseBehavior !== '') {
      purchaseBehavior = feeRow.purchaseBehavior;
    }

    if (typeof feeRow?.leaseBehavior === 'string' && feeRow.leaseBehavior !== '') {
      leaseBehavior = feeRow.leaseBehavior;
    }

    if (typeof feeRow?.financeBehavior === 'string' && feeRow.financeBehavior !== '') {
      financeBehavior = feeRow.financeBehavior;
    }

    if (typeof feeRow?.enabled === 'boolean') {
      enabled = feeRow.enabled;
    }

    if (typeof feeRow?.finalPriceText === 'string' && feeRow.finalPriceText !== '') {
      finalPriceText = feeRow.finalPriceText;
    }

    return new UntypedFormGroup({
      id: new UntypedFormControl(feeRow?.id ?? null),
      type: new UntypedFormControl(feeType),
      customFeeName: new UntypedFormControl(customFeeName),
      value: new UntypedFormControl(feeRow?.value ?? null),
      isPercent: new UntypedFormControl(isPercent),
      purchaseBehavior: new UntypedFormControl(purchaseBehavior),
      purchaseDisplayName: new UntypedFormControl(feeRow?.purchaseDisplayName ?? null),
      leaseBehavior: new UntypedFormControl(leaseBehavior),
      financeBehavior: new UntypedFormControl(financeBehavior),
      isDefaultDocumentFee: new UntypedFormControl(isDefaultDocumentFee),
      sortOrder: new UntypedFormControl(feeRow?.sortOrder ?? index),
      enabled: new UntypedFormControl(enabled),
      accountBusinessCategoryGuid: new UntypedFormControl(feeRow?.accountBusinessCategoryGuid ?? null),
      finalPriceText: new UntypedFormControl(finalPriceText)
    });
  }

  /**
   * Get Fee Type
   * @private
   */
  private getDefaultDynamicFeeType(): string
  {
    return this.dynamicDefaultFeeType;
  }

  /**
   * Resolve Custom Name
   * @param feeRow
   * @private
   */
  private resolveDynamicCustomFeeName(feeRow): string | null
  {
    let customFeeName = null;

    if (typeof feeRow?.customFeeName === 'string' && feeRow.customFeeName !== '') {
      customFeeName = feeRow.customFeeName;
    }

    if (customFeeName === null && typeof feeRow?.purchaseDisplayName === 'string' && feeRow.purchaseDisplayName !== '') {
      customFeeName = feeRow.purchaseDisplayName;
    }

    if (customFeeName === null && typeof feeRow?.displayName === 'string' && feeRow.displayName !== '') {
      customFeeName = feeRow.displayName;
    }

    return customFeeName;
  }

  /**
   * Build out FormControls and FormGroups for ad type settings form
   *
   * @param combinedFormItems
   */
  buildFormGroups(combinedFormItems) {
    const masterFormGroup = {};
    Object.keys(combinedFormItems).forEach((businessCategoryKey) => {
      masterFormGroup[businessCategoryKey] = {};
      const businessCategoryFormItems = combinedFormItems[businessCategoryKey];
      Object.keys(businessCategoryFormItems).forEach((settingsGroupKey) => {
          masterFormGroup[businessCategoryKey][settingsGroupKey] = businessCategoryFormItems[settingsGroupKey];
      });
      masterFormGroup[businessCategoryKey] = new UntypedFormGroup(masterFormGroup[businessCategoryKey]);
    });

    return new UntypedFormGroup(masterFormGroup);
  }

  /**
   * handler function called when Vehicle Ads switch is toggled
   */
  handleVehicleAdsEnabledToggle() {
    this.updateAdTypesAccountSettings(this.accountAdTypes.vehicleAdType);
    this.initializeBusinessCategoryConfigurations();
  }

  /**
   * handler function called when Promotional Ads switch is toggled
   */
  handlePromotionalAdsEnabledToggle() {
    this.updateAdTypesAccountSettings(this.accountAdTypes.promotionalAdType);
    this.initializeBusinessCategoryConfigurations();
    this.initializePromotionalAdsConfigurations();
  }

  /**
   * Update ad types account settings (for now just toggle Vehicle Ads)
   *
   * @param adTypesAccountSettings
   */
  updateAdTypesAccountSettings(adTypesAccountSettings) {
    // Check if need to initialize or update ad types
    if (adTypesAccountSettings.guid === null) {
      adTypesAccountSettings.guid = '';
      this.adTypesSettingsService.createAccountAdTypes(adTypesAccountSettings)
        .subscribe(() => {
            this.getAdTypesAccountSettings().then(() => {
            });
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to initialize Ad Type settings',
              life: 5000
            });
          });
    } else {
      this.adTypesSettingsService.updateAdTypesAccountSettings(adTypesAccountSettings)
        .subscribe(
          () => {
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to save Ad type setting.',
              life: 5000
            });
          });
    }
  }

  /**
   * Initialize & populate business category configurations when Vehicle Ads is turned on for the first time
   */
  initializeBusinessCategoryConfigurations() {
    if ((this.accountAdTypes.vehicleAdType.enabled || this.accountAdTypes.promotionalAdType.enabled)
      && this.businessCategoryConfigurations === null) {
      this.businessCategoryConfigurationsService.initializeBusinessCategoryConfigurations(this.componentName, this.accountId)
        .subscribe(
          () => {
            this.getBusinessCategoryConfigurations(this.accountId);
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to initialize configurations.',
              life: 5000
            });
          });
    }
  }

  /**
   * Initialize & populate promotional ads configurations when Promotional Ads is turned on for the first time
   */
  initializePromotionalAdsConfigurations() {
    if (this.accountAdTypes.promotionalAdType.enabled && this.promotionalAdsConfigurations === null) {
      this.adTypesSettingsService.initializePromotionalAdsConfigurations(this.accountId)
        .subscribe(
          () => {
            this.getPromotionalAdsConfigurations(this.accountId);
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to initialize configurations.',
              life: 5000
            });
          });
    }
  }

  /**
   * Vehicle Ads save form
   */
  handleVehicleAdsSave(): void
  {
    const shouldDisplayDocumentFeeWarning = this.shouldDisplayDocumentFeeWarning();

    if (shouldDisplayDocumentFeeWarning) {
      this.openDocumentFeeWarningModal();
    }

    if (!shouldDisplayDocumentFeeWarning) {
      this.saveVehicleAds();
    }
  }

  private saveVehicleAds(): void
  {
    const updatedConfigurations = this.mapConfigurationObject();

    this.businessCategoryConfigurationsService.updateBusinessCategoryConfigurations(updatedConfigurations)
      .subscribe(
        () => {
          this.masterFormGroup.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: 'Update complete',
            detail: 'Your changes were saved.',
            life: 5000
          });
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unable to save changes.',
            life: 5000
          });
        });

    this.handleOfferRulesSave();
    this.handleConditionalIncentiveSave();
  }

  /**
   * @private
   */
  private shouldDisplayDocumentFeeWarning(): boolean
  {
    let shouldDisplayDocumentFeeWarning = false;

    if (this.dynamicFeesTableEnabled && this.masterFormGroup !== null) {
      shouldDisplayDocumentFeeWarning = this.hasMisconfiguredDocumentFee();
    }

    return shouldDisplayDocumentFeeWarning;
  }

  /**
   * @private
   */
  private hasMisconfiguredDocumentFee(): boolean
  {
    let hasMisconfiguredDocumentFee = false;
    const businessCategoryKeys = Object.keys(this.masterFormGroup.controls);

    businessCategoryKeys.forEach((businessCategoryKey) => {
      const feesConfiguration = this.masterFormGroup.get([businessCategoryKey, 'feesConfiguration']) as UntypedFormGroup | null;

      if (this.isDocumentFeeMisconfigured(feesConfiguration)) {
        hasMisconfiguredDocumentFee = true;
      }
    });

    return hasMisconfiguredDocumentFee;
  }

  /**
   * @param feesConfiguration
   * @private
   */
  private isDocumentFeeMisconfigured(feesConfiguration: UntypedFormGroup | null): boolean
  {
    let isDocumentFeeMisconfigured = false;
    const documentFeeRows = this.getDocumentFeeRows(feesConfiguration);

    if (documentFeeRows.length === 0) {
      isDocumentFeeMisconfigured = true;
    }

    if (documentFeeRows.length > 0 && !this.hasConfiguredDocumentFeeValue(documentFeeRows)) {
      isDocumentFeeMisconfigured = true;
    }

    return isDocumentFeeMisconfigured;
  }

  /**
   * @param feesConfiguration
   * @private
   */
  private getDocumentFeeRows(feesConfiguration: UntypedFormGroup | null): UntypedFormGroup[]
  {
    const documentFeeRows: UntypedFormGroup[] = [];
    const feeRowsControl = feesConfiguration?.get('rows');

    if (feeRowsControl instanceof UntypedFormArray) {
      feeRowsControl.controls.forEach((feeRowControl) => {
        const feeRow = feeRowControl as UntypedFormGroup;
        const feeType = feeRow.get('type')?.value;

        if (feeType === this.dynamicDocumentFeeType) {
          documentFeeRows.push(feeRow);
        }
      });
    }

    return documentFeeRows;
  }

  /**
   * @param documentFeeRows
   * @private
   */
  private hasConfiguredDocumentFeeValue(documentFeeRows: UntypedFormGroup[]): boolean
  {
    let hasConfiguredDocumentFeeValue = false;

    documentFeeRows.forEach((documentFeeRow) => {
      const feeValue = documentFeeRow.get('value')?.value;

      if (this.isPositiveFeeValue(feeValue)) {
        hasConfiguredDocumentFeeValue = true;
      }
    });

    return hasConfiguredDocumentFeeValue;
  }

  /**
   * @param feeValue
   * @private
   */
  private isPositiveFeeValue(feeValue: unknown): boolean
  {
    let isPositiveFeeValue = false;
    const parsedFeeValue = parseFloat(String(feeValue));

    if (!Number.isNaN(parsedFeeValue) && parsedFeeValue > 0) {
      isPositiveFeeValue = true;
    }

    return isPositiveFeeValue;
  }

  /**
   * @private
   */
  private openDocumentFeeWarningModal(): void
  {
    this.confirmationService.confirm({
      key: this.documentFeeWarningModalKey,
      defaultFocus: 'none',
      message: this.documentFeeWarningMessage,
      header: this.documentFeeWarningHeader,
      rejectButtonProps: {
        label: 'Return to Fee Configuration',
        severity: 'secondary'
      },
      acceptButtonProps: {
        label: 'Save Without Document Fee',
        severity: 'warn'
      },
      accept: () => {
        this.confirmationService.close();
        this.saveVehicleAds();
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Method to handle the save of the conditional incentive form
   */
  handleConditionalIncentiveSave() {
    this.conditionalIncentiveComponents.forEach((conditionalIncentiveComponent) => {
      conditionalIncentiveComponent.saveIncentives();
    });
    this.dirtyConditionalIncentiveForms = 0;
  }

  /**
   * Helper function to map the form values to the object used in the request body
   */
  mapConfigurationObject(): object {
    const updatedConfiguration = this.businessCategoryConfigurations;
    const rawFormValues = this.masterFormGroup.getRawValue() as Record<string, BusinessCategoryConfigurationFormValue>;
    const formValuesMap = new Map<string, BusinessCategoryConfigurationFormValue>(Object.entries(rawFormValues));

    formValuesMap.forEach((configurationValue, key) => {
      const businessCategoryType = updatedConfiguration.businessCategoryTypes.find(item => item.name === key);

      if (businessCategoryType !== undefined) {
        const nestedConfiguration = configurationValue;

        nestedConfiguration.feesConfiguration = this.convertFeesToNumber(nestedConfiguration.feesConfiguration);
        businessCategoryType.configuration = nestedConfiguration;
      }
    });

    return updatedConfiguration;
  }

  /**
   * Helper function to convert fee values from strings to numbers since the Prime Faces
   * form component converts numeric input fields to strings when calling getValue()
   *
   * @param feesConfiguration
   */
  convertFeesToNumber(feesConfiguration: FeesConfigurationFormValue): FeesConfigurationFormValue {
    const convertedConfigurations = feesConfiguration;

    convertedConfigurations.documentFee.value = +convertedConfigurations.documentFee.value;
    convertedConfigurations.licenseFee.value = +convertedConfigurations.licenseFee.value;
    convertedConfigurations.titleFee.value = +convertedConfigurations.titleFee.value;
    convertedConfigurations.acquisitionFee.value = +convertedConfigurations.acquisitionFee.value;
    convertedConfigurations.taxes.value = +convertedConfigurations.taxes.value;
    convertedConfigurations.dispositionFee.value = +convertedConfigurations.dispositionFee.value;
    convertedConfigurations.customFee.value = +convertedConfigurations.customFee.value;

    if (Array.isArray(convertedConfigurations.rows)) {
      convertedConfigurations.rows.forEach((feeRow) => {
        if (feeRow.value === '') {
          feeRow.value = null;
        }

        if (feeRow.value !== null) {
          feeRow.value = +feeRow.value;
        }
      });
    }

    return convertedConfigurations;
  }

  handlePromotionalAdsSave(): void {
    let updatedConfigurations = this.mapPromotionalConfigurations();
    updatedConfigurations[0].accountId = this.accountId;
    this.adTypesSettingsService.updatePromotionalAdTypeConfigurations(updatedConfigurations[0])
      .subscribe(
        () => {
          this.promotionalAdMasterFormGroup.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: 'Update complete',
            detail: 'Your changes were saved.',
            life: 5000
          });
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unable to save promotional ad changes.',
            life: 5000
          });
        });
  }

  /**
   * Function to get promotional ads configurations and set up promotional ads config form group
   * @param accountId
   */
  getPromotionalAdsConfigurations(accountId: number) {
    if (!this.promotionalAdsConfigurations) {
      this.adTypesSettingsService.getPromotionalAdsConfigurations(accountId)
        .subscribe((promotionalAdsConfigResult: PromotionalAdsConfigurationInterface[]) => {
          this.promotionalAdsConfigurations = promotionalAdsConfigResult;
          const existingCallsToAction = this.buildCallToActionFormGroup();
          this.callsToAction = this.formBuilder.array(existingCallsToAction);
          const configuration = this.promotionalAdsConfigurations[0];
          this.promotionalAdMasterFormGroup = this.formBuilder.group({
            disclosureAddon: new UntypedFormControl(configuration.disclosureText),
            subcategories: new UntypedFormControl(this.buildMultiselectOptions(configuration.businessSubcategories)),
            callsToAction: this.callsToAction
          });
          this.buildPromotionalCategoryTabs(this.promotionalAdsConfigurations);
        });
    }
  }

  /**
   * Function to populate promotional ads tabs - currently limited to one category
   * Will
   * @param promotionalAdCategories
   */
  buildPromotionalCategoryTabs(promotionalAdCategories) {
    promotionalAdCategories.forEach((category) => {
      const promotionalAdCategory = {
        label: `${category.accountBusinessCategoryName}`,
        id: category.guid
      };
      this.promotionalAdTabs.push(promotionalAdCategory);
    });

    if (this.promotionalAdTabs.length > 0) {
      this.promotionalActiveTabValue = this.promotionalAdTabs[0].id;
    }
  }

  /**
   * Function to build promotional ads configuration call to action form group array
   * The result of this function is used to generate the FormArray for callsToAction
   */
  buildCallToActionFormGroup(): UntypedFormGroup[] {
    const callToActionForms = [];
    const configuration = this.promotionalAdsConfigurations[0];
    if (configuration.promotionalAdCallToActions !== undefined) {
      configuration.promotionalAdCallToActions.forEach((callToAction) => {
        callToActionForms.push(this.createCallToActionForm(callToAction));
      });
    }

    return callToActionForms;
  }

  /**
   * Build array of options for multi-select inputs
   *
   * @param subcategoryParameters
   */
  buildMultiselectOptions(subcategoryParameters) {
    const selectedItems = [];
    Object.keys(subcategoryParameters).forEach((key) => {
      const option = subcategoryParameters[key];
      selectedItems.push({
        name: option.name,
        id: option.id
      });
    });

    return selectedItems;
  }

  /**
   * helper function to create call to action form group
   *
   * @param callToAction
   */
  createCallToActionForm(callToAction): UntypedFormGroup {
    return this.formBuilder.group({
      target: new UntypedFormControl({value: callToAction.target}, [emptyDropdownValidator()]),
      actionUrl: new UntypedFormControl(callToAction.url),
      callToActionText: new UntypedFormControl(callToAction.displayText, [Validators.required]),
      actionOptions: new UntypedFormControl({value: callToAction.action}, [emptyDropdownValidator()]),
      order: new UntypedFormControl(callToAction.order)
    });
  }

  /**
   * Add call to action to callsToAction array
   */
  addNewCallToAction() {
    const callsToAction = this.promotionalAdMasterFormGroup.get('callsToAction') as UntypedFormArray;
    const newCallToAction = new PromotionalAdCallToAction();
    callsToAction.push(this.createCallToActionForm(newCallToAction));
    this.promotionalAdMasterFormGroup.markAsDirty();
  }

  /**
   * Delete call to action at CTA form array
   * @param index
   */
  deleteCallToAction(index) {
    const callsToAction = this.promotionalAdMasterFormGroup.get('callsToAction') as UntypedFormArray;
    callsToAction.removeAt(index);
    this.promotionalAdMasterFormGroup.markAsDirty();
    if (callsToAction.length === 0) {
      this.addNewCallToAction();
    }
  }

  /**
   * Function called on drop of reordered call to action
   * @param event
   */
  callToActionDrop(event: CdkDragDrop<string[]>) {
    const callsToAction = this.promotionalAdMasterFormGroup.get('callsToAction') as UntypedFormArray;
    this.moveItemInFormArray(callsToAction, event.previousIndex, event.currentIndex);
    this.promotionalAdMasterFormGroup.markAsDirty();
  }


  /**
   * Moves an item in a FormArray to another position
   * @param formArray
   * @param fromIndex
   * @param toIndex
   */
  moveItemInFormArray(
    formArray: UntypedFormArray,
    fromIndex: number,
    toIndex: number
  ): void {
    const direction = toIndex > fromIndex ? 1 : -1;

    const item = formArray.at(fromIndex);
    for (let iteration = fromIndex; iteration * direction < toIndex * direction; iteration = iteration + direction) {
      const current = formArray.at(iteration + direction);
      formArray.setControl(iteration, current);
    }
    formArray.setControl(toIndex, item);
  }

  /**
   * Function to convert promotional ad configuration properties to format to be saved
   */
  mapPromotionalConfigurations(): object {
    const updatedConfigurations = this.promotionalAdsConfigurations;
    const updatedCallsToAction = [];
    const callsToActionMap = new Map(Object.entries(this.promotionalAdMasterFormGroup.get('callsToAction').value));
    callsToActionMap.forEach((ctaValue, index) => {
      let callToActionReformatted = new PromotionalAdCallToAction();
      callToActionReformatted.action = ctaValue['actionOptions'].value;
      callToActionReformatted.displayText = ctaValue['callToActionText'];
      callToActionReformatted.url = ctaValue['actionUrl'];
      if (callToActionReformatted.url == null){
        callToActionReformatted.url = '';
      }
      callToActionReformatted.order = +index;
      callToActionReformatted.target = ctaValue['target'].value;
      callToActionReformatted.promotionalAdsConfigurationGuid = updatedConfigurations[0].guid;
      updatedCallsToAction.push(callToActionReformatted);
    });

    updatedConfigurations[0].disclosureText = this.promotionalAdMasterFormGroup.get('disclosureAddon').value;
    updatedConfigurations[0].businessSubcategories = this.promotionalAdMasterFormGroup.get('subcategories').value;
    updatedConfigurations[0].promotionalAdCallToActions = updatedCallsToAction;

    return updatedConfigurations;
  }

  /**
   * Method to handle the dirty state of the offer rules form
   * @param isDirty
   */
  handleOfferRulesFormDirty(isDirty: boolean) {
    if (isDirty) {
      this.dirtyOfferRulesForms += 1;
    } else {
      this.dirtyOfferRulesForms -= 1;
    }
  }

  /**
   * Gets whether the save button should be disabled or not
   */
  get isSaveDisabled(): boolean {
    let isSaveDisabled = true;

    if (this.masterFormGroup !== null) {
      isSaveDisabled = !this.hasUnsavedVehicleAdsChanges()
        || !this.masterFormGroup.valid
        || !this.conditionalIncentiveFormValid;
    }

    return isSaveDisabled;
  }

  /**
   * @private
   */
  private hasUnsavedVehicleAdsChanges(): boolean
  {
    return (this.masterFormGroup !== null && this.masterFormGroup.dirty) || this.dirtyOfferRulesForms > 0 || this.dirtyConditionalIncentiveForms > 0;
  }

  /**
   * Method to handle the dirty state of the conditional incentive form
   * @param isDirty
   */
  handleConditionalIncentiveFormDirty(isDirty: boolean) {
    if (isDirty === true) {
      this.dirtyConditionalIncentiveForms += 1;
    } else if (isDirty === false) {
      this.dirtyConditionalIncentiveForms -= 1;
    }
  }

  /**
   * Method to handle the validity state of the conditional incentive form
   * @param isValid
   */
  setConditionalIncentiveFormValid(isValid: boolean) {
    this.conditionalIncentiveFormValid = isValid;
  }

  /**
   * Method to handle the save of the offer rules form
   */
  handleOfferRulesSave() {
    this.offerRulesComponents.forEach((offerRulesComponent) => {
      offerRulesComponent.saveRules();
    });
    this.dirtyOfferRulesForms = 0;
  }

  /**
   * Method to handle the beforeunload event to prevent users from navigating away from the page
   * @param event
   */
  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: Event): void {
    if (this.dirtyOfferRulesForms > 0 || this.dirtyConditionalIncentiveForms > 0) {
      event.preventDefault();
    }
  }

  /**
   * Method to handle the delete of the offer rules
   */
  handleDeleteOfferRules() {
    this.messageService.add({
      severity: 'success',
      summary: 'Delete Successful',
      detail: 'Ad Rule(s) deleted successfully.',
      life: 5000
    });
  }

  /**
   * Method to handle toggling of enhanced incentives
   */
  handleEnhancedIncentivesToggle() {
    this.messageService.add({
      severity: 'warn',
      summary: 'Warning: Action Required',
      detail: 'Please delete and re-create all New ads after turning Enhanced Incentives on or off.',
      life: 10000
    });
  }

  /**
   * Sets the controls for the incentive toggles that are dependent on the use incentive service setting
   */
  setIncentiveControlsStatus() {
    if (this.formItems['New'].incentiveConfiguration.controls.useIncentiveService.value === false) {
      this.formItems['New'].incentiveConfiguration.controls.enhancedIncentives.disable();
      this.formItems['New'].incentiveConfiguration.controls.useVinSpecificIncentives.disable();
      this.formItems['New'].generalConfiguration.controls.includeSelectInventoryIncentives.disable();
      this.formItems['New'].incentiveConfiguration.controls.enhancedIncentives.setValue(false);
      this.formItems['New'].incentiveConfiguration.controls.useVinSpecificIncentives.setValue(false);
      this.formItems['New'].generalConfiguration.controls.includeSelectInventoryIncentives.setValue(false);
    } else {
      this.formItems['New'].incentiveConfiguration.controls.enhancedIncentives.enable();
      this.formItems['New'].incentiveConfiguration.controls.useVinSpecificIncentives.enable();
      this.formItems['New'].generalConfiguration.controls.includeSelectInventoryIncentives.enable();
    }
  }

  /**
   * Gets the disabled tool tip if criteria is not met
   */
  getDisabledToolTip() {
    let result = null;

    if (this.masterFormGroup !== null && !this.masterFormGroup.valid) {
      result = 'Please complete all required fields before saving.';
    }

    if (this.masterFormGroup !== null && this.masterFormGroup.valid && !this.hasUnsavedVehicleAdsChanges()) {
      result = 'Make a change before saving.';
    }

    return result;
  }
}
